import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  formatPackagingVolumeCm3ForInput,
} from '@/shared/components/packaging/componentNumericInput'
import type {
  PackagingItem,
  PackagingPpwrComplianceLevel,
} from '@/shared/hooks/api/packagingSuppliers'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { PackagingItemSkuCell } from './PackagingItemSkuCell'

function formatPpwrComplianceLevelLabel(
  level: PackagingPpwrComplianceLevel | null | undefined,
  labels: {
    none: string
    sufficientlyMinimized: string
    notSufficientlyMinimized: string
  }
): string | null {
  if (level == null) {
    return null
  }
  if (level === 'NONE') {
    return labels.none
  }
  if (level === 'SUFFICIENTLY_MINIMIZED') {
    return labels.sufficientlyMinimized
  }
  return labels.notSufficientlyMinimized
}

type PackagingItemMetadataItemGridProps = {
  item: PackagingItem
  manufacturerLabels: {
    question: string
    optionYes: string
    optionNo: string
    manufacturerName: string
    manufacturerAddress: string
  }
  ppwrComplianceLabels: {
    heavyMetals: string
    substancesOfConcern: string
    none: string
    sufficientlyMinimized: string
    notSufficientlyMinimized: string
  }
}

function MetadataItem({
  title,
  description,
  className,
}: {
  title: string
  description: ReactNode
  className?: string
}) {
  return (
    <Item variant='outline' className={className}>
      <ItemContent>
        <ItemTitle>{title}</ItemTitle>
        <ItemDescription className='whitespace-pre-wrap'>
          {description}
        </ItemDescription>
      </ItemContent>
    </Item>
  )
}

export function PackagingItemMetadataItemGrid({
  item,
  manufacturerLabels,
  ppwrComplianceLabels,
}: PackagingItemMetadataItemGridProps) {
  const { t } = useTranslation('packagingSuppliers')
  const emptyLabel = t('common:notAvailable')

  let manufacturerAnswerLabel: string | null = null
  if (item.supplierIsManufacturer === true) {
    manufacturerAnswerLabel = manufacturerLabels.optionYes
  } else if (item.supplierIsManufacturer === false) {
    manufacturerAnswerLabel = manufacturerLabels.optionNo
  }

  let packagingTypeLabel: string | null = null
  if (item.packagesOwnProduct === false) {
    packagingTypeLabel = t('itemDetail.packagingTypeStandalone')
  } else if (item.packagesOwnProduct === true) {
    packagingTypeLabel = t('itemDetail.packagingTypeForPart')
  }

  const volumeCm3Label =
    item.volumeCm3 != null
      ? `${formatPackagingVolumeCm3ForInput(item.volumeCm3)} cm³`
      : null

  const display = (value: string | null | undefined) =>
    value?.trim() ? value : emptyLabel

  return (
    <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
      <MetadataItem
        title={t('items.form.sku')}
        description={
          item.sku?.trim() ? (
            <PackagingItemSkuCell
              sku={item.sku}
              skuInBomList={item.skuInBomList ?? false}
            />
          ) : (
            emptyLabel
          )
        }
      />
      <MetadataItem
        title={t('itemDetail.packagingType')}
        description={display(packagingTypeLabel)}
      />
      <MetadataItem
        title={manufacturerLabels.question}
        description={display(manufacturerAnswerLabel)}
      />
      <MetadataItem
        title={manufacturerLabels.manufacturerName}
        description={display(item.manufacturerName)}
      />
      <MetadataItem
        title={manufacturerLabels.manufacturerAddress}
        description={display(item.manufacturerAddress)}
      />
      <MetadataItem
        className='sm:col-span-2 lg:col-span-3'
        title={t('items.form.description')}
        description={display(item.description)}
      />
      <MetadataItem
        className='sm:col-span-2 lg:col-span-3'
        title={t('items.form.intendedUse')}
        description={display(item.intendedUse)}
      />
      <MetadataItem
        className='opacity-60'
        title={t('items.form.packedItemSku')}
        description={display(item.packedItemSku)}
      />
      <MetadataItem
        title={t('items.form.volumeCm3')}
        description={display(volumeCm3Label)}
      />
      <MetadataItem
        title={ppwrComplianceLabels.heavyMetals}
        description={display(
          formatPpwrComplianceLevelLabel(
            item.heavyMetalsStatus,
            ppwrComplianceLabels
          )
        )}
      />
      <MetadataItem
        title={ppwrComplianceLabels.substancesOfConcern}
        description={display(
          formatPpwrComplianceLevelLabel(
            item.substancesOfConcernStatus,
            ppwrComplianceLabels
          )
        )}
      />
    </div>
  )
}
